import { useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
  AlertCircle,
  BadgeQuestionMark,
  BookOpen,
  Building2,
  CalendarClock,
  CalendarPlus,
  DollarSign,
  FileCheck2,
  LayoutGrid,
  Mail,
  Receipt,
  RefreshCw,
  Send,
  Sparkles,
  Wallet,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCondensedHeader } from '@/hooks/useCondensedHeader';
import { centralToday } from '@/lib/businessTime';
import { exportToExcel } from '@/lib/excel-utils';
import { Skeleton } from '@/components/ui/skeleton';
import { WORKFLOW_TABS } from '@/pages/WorkflowV2/config/workflowTabs';
import { formatCell } from '@/pages/WorkflowV2/format';
import { rowAssigneeNames } from '@/pages/WorkflowV2/components/AssigneeFilter';
import type { WorkflowRow } from '@/pages/WorkflowV2/api';
import MonthPicker, {
  booksMonthLabel,
  workflowMonthLabel,
} from './components/MonthPicker';
import BookkeepingStatusCards from './components/BookkeepingStatusCards';
import AssigneeProgressCards, {
  ASSIGNEE_THEME,
} from './components/AssigneeProgressCards';
import QuickAccessCard from './components/QuickAccessCard';
import { IconBadge } from './components/Badges';
import {
  CycleBar,
  ViewToggle,
  type DashboardView,
} from './components/HeaderControls';
import AnnouncementsCard from './widgets/AnnouncementsCard';
import AppointmentsCard from './widgets/AppointmentsCard';
import QueueCard from './widgets/QueueCard';
import PipelineCard from './widgets/PipelineCard';
import ActivityCard from './widgets/ActivityCard';
import { useDashboardData } from './useDashboardData';
import {
  MINE_COLUMNS,
  SERVICE_TABS,
  askMyAccountantQueue,
  assigneeProgress,
  bookkeepingPipeline,
  bookkeepingStatusCounts,
  cycleProgress,
  hasAnyAssignment,
  pickupsReady,
  previousMonthKey,
  rowsAssignedTo,
  rowsStampedBy,
  serviceRows,
  workflowLink,
  type ServiceTabId,
} from './stats';

// Mirrors Layout's role gates: Email is Admin+ (2026-08-28), ACH Payors is
// Manager+
const isAdminPlus = (role: string | null) =>
  role === 'SuperAdmin' || role === 'Admin';
const isManagerPlus = (role: string | null) =>
  isAdminPlus(role) || role === 'Manager';

// Per-browser preferences (localStorage; a blocked store just means defaults)
const VIEW_KEY = 'dashboard-view';

function readPref<T extends string>(key: string, allowed: T[]): T | null {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as string[]).includes(v) ? (v as T) : null;
  } catch {
    return null;
  }
}

function writePref(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: preference lasts for this visit only
  }
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function Section({
  title,
  description,
  icon: Icon,
  action,
  className = 'px-4 sm:px-8',
  panel = false,
  children,
}: Readonly<{
  title: string;
  description?: string;
  icon: ComponentType<{ className?: string }>;
  action?: ReactNode;
  /** Outer padding; '' when a parent grid already pads */
  className?: string;
  /** Sit on a soft rounded panel, to separate the status sections */
  panel?: boolean;
  children: ReactNode;
}>) {
  return (
    <section className={`min-w-0 ${className}`} aria-label={title}>
      <div
        className={
          panel
            ? 'h-full rounded-2xl bg-white/50 p-4 ring-1 ring-black/5 sm:p-5'
            : undefined
        }
      >
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 font-serif">
              <IconBadge icon={Icon} />
              {title}
            </h2>
            {description && (
              <p className="mt-0.5 text-sm text-slate-500">{description}</p>
            )}
          </div>
          {action}
        </div>
        {children}
      </div>
    </section>
  );
}

/** The section's rows, narrowed to one employee's in My work view */
function scopeRows(
  source: WorkflowRow[],
  tab: ServiceTabId,
  name: string | null
): WorkflowRow[] {
  return name ? rowsAssignedTo(source, name, MINE_COLUMNS[tab]) : source;
}

/** Card-shaped placeholders for the first load (no misleading zeros) */
function CardSkeletons({
  count,
  className,
}: Readonly<{ count: number; className: string }>) {
  return (
    <div className={className} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-[124px] rounded-xl" />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { profile, employeeRole } = useAuth();
  const navigate = useNavigate();
  const {
    months,
    selectedMonth,
    previousMonth,
    selectMonth,
    rows,
    prevRows,
    columns,
    employees,
    initialLoading,
    loading,
    error,
    loadedAt,
    refresh,
  } = useDashboardData();

  const todayIso = centralToday();

  // Condense the sticky header once the page scrolls past its top
  const { sentinelRef: headerSentinel, stuck: headerStuck } =
    useCondensedHeader();
  // yyyy-MM of the selected workflow month — carried into every Workflow V2
  // link so it opens on the month the Dashboard is showing
  const monthKey = selectedMonth?.month.slice(0, 7) ?? null;
  const prevLabel = previousMonth
    ? format(parseISO(previousMonth.month.slice(0, 10)), 'MMM')
    : 'last month';

  // ---- My work / Team -----------------------------------------------------
  // Workflow assignee values are stored as UPPERCASE employee names
  const myName = profile?.name?.trim().toUpperCase() || null;
  const iHaveWork = useMemo(
    () =>
      myName
        ? hasAnyAssignment(rows, myName) ||
          (monthKey !== null &&
            rowsStampedBy(rows, myName, previousMonthKey(monthKey)).length > 0)
        : false,
    [rows, myName, monthKey]
  );
  const [storedView, setStoredView] = useState(() =>
    readPref<DashboardView>(VIEW_KEY, ['mine', 'team'])
  );
  // Default: managers see the team; everyone else their own work (when the
  // month has any of it)
  const view: DashboardView =
    storedView ?? (isManagerPlus(employeeRole) || !iHaveWork ? 'team' : 'mine');
  const mine = view === 'mine' && myName !== null;
  const changeView = (v: DashboardView) => {
    setStoredView(v);
    writePref(VIEW_KEY, v);
  };

  // Whose accounts the sections count: mine (My work) or everyone's
  const scopeName = mine ? myName : null;

  // ---- Stats ----------------------------------------------------------------
  // Bookkeeping's My work = accounts I stamped for the books month
  const booksKey = monthKey ? previousMonthKey(monthKey) : null;
  const prevBooksKey = booksKey ? previousMonthKey(booksKey) : null;
  const bookkeeping = useMemo(
    () =>
      bookkeepingStatusCounts(
        scopeName && booksKey ? rowsStampedBy(rows, scopeName, booksKey) : rows
      ),
    [rows, scopeName, booksKey]
  );
  const prevBookkeeping = useMemo(() => {
    if (!prevRows) return null;
    const { counts, total } = bookkeepingStatusCounts(
      scopeName && prevBooksKey
        ? rowsStampedBy(prevRows, scopeName, prevBooksKey)
        : prevRows
    );
    return {
      byValue: new Map(counts.map(c => [c.option.value, c.count])),
      total,
    };
  }, [prevRows, scopeName, prevBooksKey]);
  const salesTax = useMemo(
    () =>
      assigneeProgress(scopeRows(rows, 'sales-tax', scopeName), 'sales-tax'),
    [rows, scopeName]
  );
  const prevSalesTax = useMemo(
    () =>
      prevRows
        ? assigneeProgress(
            scopeRows(prevRows, 'sales-tax', scopeName),
            'sales-tax'
          )
        : null,
    [prevRows, scopeName]
  );
  const payroll = useMemo(
    () => assigneeProgress(scopeRows(rows, 'payroll', scopeName), 'payroll'),
    [rows, scopeName]
  );
  const prevPayroll = useMemo(
    () =>
      prevRows
        ? assigneeProgress(scopeRows(prevRows, 'payroll', scopeName), 'payroll')
        : null,
    [prevRows, scopeName]
  );
  const pipeline = useMemo(
    () => (booksKey ? bookkeepingPipeline(rows, booksKey, scopeName) : null),
    [rows, booksKey, scopeName]
  );
  const amaQueue = useMemo(
    () => askMyAccountantQueue(scopeRows(rows, 'bookkeeping', scopeName)),
    [rows, scopeName]
  );
  const pickups = useMemo(() => pickupsReady(rows), [rows]);

  // ---- Navigation -----------------------------------------------------------
  const openWorkflow = (
    tab?: string,
    filter: {
      status?: string;
      assignee?: string | null;
      stamped?: string | null;
      flag?: 'ama';
      open?: string;
    } = {}
  ) => navigate(workflowLink({ month: monthKey, tab, ...filter }));

  /** My work keeps the assignee filter when opening a section's tab */
  const myFilter = mine ? { assignee: myName } : {};
  /** Bookkeeping's My work opens the accounts I stamped */
  const myStampedFilter = mine ? { stamped: myName } : {};

  /** Excel export of one employee's (or the unassigned) service accounts */
  const handleExport = async (
    tab: 'sales-tax' | 'payroll',
    name: string | null
  ) => {
    const { statusField, assigneeColumn } = SERVICE_TABS[tab];
    const accounts = serviceRows(rows, tab).filter(r => {
      const names = rowAssigneeNames(r.data, [assigneeColumn]);
      return name ? names.has(name) : names.size === 0;
    });
    if (accounts.length === 0) return;
    const tabColumns = WORKFLOW_TABS.find(t => t.id === tab)?.columns ?? [];
    const headers = [
      'Legal Business Name',
      statusField.column,
      ...tabColumns.filter(c => columns.some(col => col.name === c)),
    ];
    const typeOf = (c: string) =>
      columns.find(col => col.name === c)?.column_type ?? 'text';
    const exportRows = accounts.map(r =>
      Object.fromEntries(
        headers.map(h => {
          const cell = formatCell(h, typeOf(h), r.data[h]);
          const text = cell.kind === 'empty' ? '' : cell.text;
          return [h, cell.sub ? `${text} (${cell.sub})` : text];
        })
      )
    );
    try {
      await exportToExcel(
        { headers, rows: exportRows },
        `${tab}-${(name ?? 'unassigned').toLowerCase().replace(/\s+/g, '-')}-${monthKey ?? 'month'}`
      );
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export. Please try again.');
    }
  };

  const firstName =
    profile?.name?.split(' ')[0] ||
    profile?.email?.replace('@tifpaconsulting.com', '') ||
    'there';

  // ---- Sections -------------------------------------------------------------
  const todayRow = (
    <div className="grid grid-cols-1 gap-4 px-4 sm:px-8 md:grid-cols-2 xl:grid-cols-3">
      <AppointmentsCard
        onOpen={() => navigate('/appointments')}
        onSchedule={() => navigate('/appointments?new=1')}
      />
      <QueueCard
        title="Ready for Pickup"
        icon={FileCheck2}
        items={pickups}
        loading={initialLoading}
        emptyText="No printed financial statements are waiting for pickup."
        assigneeLabel="Prepared by"
        formatDetail={month =>
          `printed ${format(parseISO(`${month}-01`), 'MMM yyyy')}`
        }
        onOpenItem={rowId => openWorkflow('tifpa-tasks', { open: rowId })}
        onViewAll={() => openWorkflow('tifpa-tasks')}
      />
      <ActivityCard
        monthId={selectedMonth?.id ?? null}
        refreshKey={loadedAt?.getTime() ?? 0}
      />
    </div>
  );

  const statusSections = (
    <>
      <Section
        title="Bookkeeping Status"
        panel
        description={
          mine
            ? 'Bookkeeping accounts you stamped a step on for the books month, by Account Status.'
            : 'Bookkeeping accounts by Account Status. Click a status to see just those accounts.'
        }
        icon={BookOpen}
      >
        {initialLoading ? (
          <CardSkeletons
            count={8}
            className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(150px,1fr))]"
          />
        ) : (
          <BookkeepingStatusCards
            counts={bookkeeping.counts}
            total={bookkeeping.total}
            loading={loading}
            previous={prevBookkeeping?.byValue}
            previousTotal={prevBookkeeping?.total}
            previousLabel={prevLabel}
            onOpen={status =>
              openWorkflow('bookkeeping', {
                ...(status === undefined ? {} : { status }),
                ...myStampedFilter,
              })
            }
          />
        )}
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <PipelineCard
            stages={pipeline?.stages ?? []}
            total={pipeline?.total ?? 0}
            booksLabel={selectedMonth ? booksMonthLabel(selectedMonth) : ''}
            loading={initialLoading}
            onOpen={() => openWorkflow('bookkeeping', myStampedFilter)}
          />
          <QueueCard
            title="Ask My Accountant"
            icon={BadgeQuestionMark}
            items={amaQueue}
            loading={initialLoading}
            emptyText="No accounts are flagged Ask My Accountant."
            assigneeLabel="Acct Mgr"
            onOpenItem={rowId => openWorkflow('bookkeeping', { open: rowId })}
            onViewAll={() =>
              openWorkflow('bookkeeping', { flag: 'ama', ...myFilter })
            }
          />
        </div>
      </Section>

      {/* Sales Tax and Payroll share a row on wide screens */}
      <div className="grid grid-cols-1 gap-6 px-4 sm:px-8 xl:grid-cols-2">
        <Section
          className=""
          title="Sales Tax Status"
          panel
          description="Completed sales tax accounts by assigned employee (Sales Tax Status = Complete)."
          icon={Receipt}
        >
          {initialLoading ? (
            <CardSkeletons
              count={4}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3"
            />
          ) : (
            <AssigneeProgressCards
              theme={ASSIGNEE_THEME}
              people={salesTax.people}
              total={salesTax.total}
              totalLabel={mine ? 'My Sales Tax' : 'Total Sales Tax'}
              totalIcon={DollarSign}
              employees={employees}
              loading={loading}
              previous={prevSalesTax}
              previousLabel={prevLabel}
              onView={name =>
                openWorkflow('sales-tax', name ? { assignee: name } : myFilter)
              }
              compact
              onExport={name => void handleExport('sales-tax', name)}
            />
          )}
        </Section>

        <Section
          className=""
          title="Payroll Status"
          panel
          description="Completed payroll accounts by assigned payroll employee (Payroll Status = Complete)."
          icon={Wallet}
        >
          {initialLoading ? (
            <CardSkeletons
              count={4}
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3"
            />
          ) : (
            <AssigneeProgressCards
              theme={ASSIGNEE_THEME}
              people={payroll.people}
              total={payroll.total}
              totalLabel={mine ? 'My Payroll' : 'Total Payroll'}
              totalIcon={Wallet}
              employees={employees}
              loading={loading}
              previous={prevPayroll}
              previousLabel={prevLabel}
              onView={name =>
                openWorkflow('payroll', name ? { assignee: name } : myFilter)
              }
              compact
              onExport={name => void handleExport('payroll', name)}
            />
          )}
        </Section>
      </div>
    </>
  );

  const quickAccess = (
    <Section
      title="Quick Access"
      description="Jump straight to the pages and tasks you use most."
      icon={LayoutGrid}
    >
      <div
        className={`grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 ${isAdminPlus(employeeRole) ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}
      >
        <QuickAccessCard
          title="TIFPA Workflow V2"
          description="All accounts, by service."
          icon={Sparkles}
          onOpen={() => openWorkflow()}
          meta={
            selectedMonth && rows.length > 0
              ? `${rows.length} accounts`
              : undefined
          }
          actions={[
            {
              label: 'View All',
              onClick: () => openWorkflow('all-accounts'),
            },
            {
              label: 'Bookkeeping',
              onClick: () => openWorkflow('bookkeeping'),
            },
            {
              label: 'Sales Tax',
              onClick: () => openWorkflow('sales-tax'),
            },
            { label: 'Payroll', onClick: () => openWorkflow('payroll') },
          ]}
        />
        <QuickAccessCard
          title="Appointments"
          description="Today's board and check-ins."
          icon={CalendarClock}
          onOpen={() => navigate('/appointments')}
          actions={[
            {
              label: 'Schedule',
              icon: CalendarPlus,
              onClick: () => navigate('/appointments?new=1'),
            },
            {
              label: 'Open Appointments',
              onClick: () => navigate('/appointments'),
            },
          ]}
        />
        {isAdminPlus(employeeRole) && (
          <QuickAccessCard
            title="Email"
            description="Client emails and templates."
            icon={Mail}
            onOpen={() => navigate('/email')}
            actions={[
              {
                label: 'Send Email',
                icon: Send,
                onClick: () => navigate('/email'),
              },
              {
                label: 'History',
                onClick: () => navigate('/email/history'),
              },
            ]}
          />
        )}
        <QuickAccessCard
          title="Bank Data"
          description="Transactions and statements."
          icon={Building2}
          onOpen={() => navigate('/bank-data-v2')}
        />
        <QuickAccessCard
          title="Sales Taxes"
          description="Monthly sales tax returns."
          icon={Receipt}
          onOpen={() => navigate('/sales-tax-v2')}
        />
      </div>
    </Section>
  );

  return (
    <div className="min-h-screen bg-[color-mix(in_oklch,var(--tifpa-light-rose)_35%,white)] pb-8 pt-5">
      {/* Scrolling past this marker condenses the sticky header */}
      <div ref={headerSentinel} aria-hidden />
      {/* Header: sticks to the top so the view toggle and month stay in reach */}
      <header
        className={`sticky top-0 z-20 flex flex-wrap justify-between gap-x-4 gap-y-2 px-4 py-3 backdrop-blur-md transition-shadow sm:px-8 ${
          headerStuck
            ? 'items-center bg-[color-mix(in_oklch,color-mix(in_oklch,var(--tifpa-light-rose)_35%,white)_75%,transparent)] shadow-sm ring-1 ring-black/5'
            : 'items-end'
        }`}
      >
        <div>
          {!headerStuck && (
            <p className="text-sm font-medium text-slate-500">
              {format(new Date(), 'EEEE, MMMM d')}
            </p>
          )}
          <h1
            className={`font-bold text-slate-900 font-serif transition-all ${
              headerStuck ? 'hidden text-lg sm:block' : 'text-3xl'
            }`}
          >
            {greeting()}, {firstName}
          </h1>
          {selectedMonth && !headerStuck && (
            <p className="mt-1 text-sm text-slate-500">
              Showing {mine ? 'your accounts in ' : ''}the{' '}
              <span className="font-medium text-slate-700">
                {workflowMonthLabel(selectedMonth)}
              </span>{' '}
              workflow ({booksMonthLabel(selectedMonth)} books). Click any card
              to open those accounts in TIFPA Workflow V2.
            </p>
          )}
          {selectedMonth && monthKey && !headerStuck && (
            <CycleBar
              progress={cycleProgress(monthKey, todayIso)}
              monthLabel={workflowMonthLabel(selectedMonth)}
            />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ViewToggle
            value={mine ? 'mine' : 'team'}
            onChange={changeView}
            mineDisabled={!myName}
          />
          <MonthPicker
            months={months}
            selected={selectedMonth}
            onSelect={selectMonth}
            disabled={loading && months.length === 0}
          />
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-modern-charcoal bg-modern-charcoal text-white shadow-sm transition-colors hover:bg-modern-light-charcoal px-3 text-sm disabled:opacity-70"
            title={
              loadedAt
                ? `Live: updates on its own. Last updated ${format(loadedAt, 'h:mm a')}`
                : 'Refresh'
            }
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">
              {loadedAt ? format(loadedAt, 'h:mm a') : 'Refresh'}
            </span>
          </button>
        </div>
      </header>

      <div className="mt-5 space-y-10">
        {/* Screen readers hear when the numbers reload */}
        <p className="sr-only" aria-live="polite">
          {loading
            ? 'Loading dashboard numbers'
            : loadedAt
              ? `Dashboard updated at ${format(loadedAt, 'h:mm a')}`
              : ''}
        </p>

        {error && (
          <div
            role="alert"
            className="mx-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 sm:mx-8"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Couldn't load the workflow numbers: {error}{' '}
              <button
                type="button"
                onClick={refresh}
                className="font-medium underline"
              >
                Try again
              </button>
            </span>
          </div>
        )}

        {!loading && months.length === 0 && !error && (
          <div className="mx-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 sm:mx-8">
            No workflow months yet. Add one from TIFPA Workflow V2.
          </div>
        )}

        <AnnouncementsCard
          canManage={isAdminPlus(employeeRole)}
          isSuperAdmin={employeeRole === 'SuperAdmin'}
        />

        {todayRow}
        {statusSections}
        {quickAccess}
      </div>
    </div>
  );
}
